import React, { useEffect, useCallback } from 'react';
import { Formik, FormikProps, Field } from 'formik';

import { ECompanyType, EMarketingSource, ENetworkRequestStatus, ICompany } from 'services/BackendApi';
import FluidButton from 'ui/FluidButton';

import { TravelCompanyFormValidationSchema } from './formValidation';
import { useDispatch, useSelector } from 'react-redux';
import {
  companyCreateNetworkSelector,
  companyUpdateNetworkSelector,
  createCompanyRequestAction,
  initCompanySaveStatusAction,
  updateCompanyRequestAction,
} from 'store/modules/companyInfo';
import { ICompanyLogoPosition } from 'store/modules/companyInfo/model';
import { useHistory } from 'react-router';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import { EUITextAreaResizeType, UITextArea } from 'ui/UITextArea';
import * as CompanyInfoSelectors from 'store/modules/companyInfo/selectors';
import Editor from 'pureUi/Editor';

export interface ITravelCompanyFormV2Props {
  company?: ICompany;
  children?: React.ReactNode;
  isEditMode?: boolean;
}

export interface ITravelCompanyFormValues {
  uuid: string | null;
  companyId: string;
  companyName: string;
  countryCode: string | null;
  address: string;
  phone: string;
  website: string;
  marketingSource: EMarketingSource | null;
  salesRepresentative: string | null;
  companyType: ECompanyType | null;
  comment: string;
  logoPosition: ICompanyLogoPosition | null;
  serviceChargeOverride: string | null;
}

export const TravelCompanyFormV2: React.FC<ITravelCompanyFormV2Props> = ({ children, company, isEditMode }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const companyCreateNetwork = useSelector(companyCreateNetworkSelector);
  const companyUpdateNetwork = useSelector(companyUpdateNetworkSelector);

  const isSaving =
    companyCreateNetwork === ENetworkRequestStatus.PENDING || companyUpdateNetwork === ENetworkRequestStatus.PENDING;
  const isAlreadySaved = companyCreateNetwork === ENetworkRequestStatus.SUCCESS;

  useEffect(() => {
    dispatch(initCompanySaveStatusAction());
  }, []);

  const companyExternalMarkupPercentage = useSelector(CompanyInfoSelectors.companyExternalMarkupPercentageSelector);
  const customServiceCharge = useSelector(CompanyInfoSelectors.customServiceChargeSelector);

  const initialValues: ITravelCompanyFormValues = {
    uuid: company?.uuid ?? null,
    companyId: company?.companyId ?? '',
    companyName: company?.name ?? '',
    countryCode: company?.countryCode ?? '',
    address: company?.address ?? '',
    phone: company?.phoneNumber ?? '',
    website: company?.website ?? '',
    marketingSource: company?.marketingSource ?? null,
    salesRepresentative: company?.salesRepresentative ?? '',
    companyType: company?.companyType ?? null,
    comment: company?.comment ?? '',
    logoPosition: company?.logoPosition ?? 'left',
    serviceChargeOverride: company?.serviceChargeOverride ?? null,
  };

  const handleCreateCompanyCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleFormSubmit = useCallback(
    (formValues: ITravelCompanyFormValues) => {
      const travelCompany: Partial<ICompany> = {
        companyId: formValues.companyId,
        name: formValues.companyName,
        countryCode: formValues.countryCode ?? '',
        address: formValues.address,
        phoneNumber: formValues.phone || null,
        website: formValues.website || null,
        marketingSource: formValues.marketingSource,
        companyType: formValues.companyType,
        comment: formValues.comment || null,
        salesRepresentative: formValues.salesRepresentative || null,
        logoPosition: formValues.logoPosition,
        serviceChargeOverride: customServiceCharge ? customServiceCharge.toString() : null,
        externalMarkupPercentage: companyExternalMarkupPercentage ? companyExternalMarkupPercentage.toString() : null,
      };
      if (company) {
        travelCompany.uuid = company.uuid;
        dispatch(updateCompanyRequestAction(company.uuid, travelCompany));
      } else {
        dispatch(createCompanyRequestAction(travelCompany, history));
      }
    },
    [dispatch, company, history, companyExternalMarkupPercentage, customServiceCharge]
  );

  return (
    <div className="travel-company-form">
      <p className="heading m-0 font-pt-sans text-black-true font-bold text-17px leading-22px">Comments</p>
      <Formik
        initialValues={initialValues}
        validationSchema={TravelCompanyFormValidationSchema}
        enableReinitialize={true}
        onSubmit={handleFormSubmit}
      >
        {(form: FormikProps<ITravelCompanyFormValues>) => {
          return (
            <form onSubmit={form.handleSubmit}>
              <div className="travel-company-form">
                <div className="comments flex flex-col mt-[10px]">
                  <Field name="comment">
                    {({ field: { name, value }, form: { setFieldValue } }) => (
                      <Editor
                        text={value}
                        handleEditorChange={(value) => setFieldValue(name, value)} 
                        options={[]} 
                        mentionAbility={false} 
                        disabled={!isEditMode}
                      />
                    )}
                  </Field>

                  {children ? children : null}

                  <div className="flex items-center mt-5">
                    {!form.values.uuid && (
                      <FluidButton
                        className="new-company-cancel-button mr-10px"
                        onClick={handleCreateCompanyCancel}
                        type="secondary"
                        disabled={isSaving || isAlreadySaved || !isEditMode}
                      >
                        Cancel
                      </FluidButton>
                    )}

                    {isEditMode && (
                      <FluidButton
                        type="primary"
                        className="new-company-confirm-button"
                        submit
                        isLoading={isSaving}
                        disabled={!form.isValid || isAlreadySaved}
                      >
                        Save
                      </FluidButton>
                    )}
                  </div>
                </div>
              </div>
              <LeaveWithoutSavingModal
                title={
                  'You have made changes to this page, and not saved. If you leave this page now, these changes will be lost.'
                }
                when={form.dirty && !isAlreadySaved && !isSaving}
                confirmButtonLabel="Yes"
                cancelButtonLabel="No"
              />
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
